import { FC, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Section } from '../../layout';
import { Button } from '../../ui/Button';
import { TestimonialCard, TestimonialCardProps } from './TestimonialCard';
import { cn } from '../../../utils/cn';

// Mock testimonial data
const mockTestimonials: Omit<TestimonialCardProps, 'className'>[] = [
  {
    quote:
      "Best massage I've ever had! Sarah really knows how to work out those knots. I leave feeling like a new person every time.",
    clientName: 'Jennifer M.',
    service: 'Deep Tissue Massage',
    rating: 5,
  },
  {
    quote:
      'The hot stone therapy was absolutely divine. Such a relaxing atmosphere and professional service.',
    clientName: 'Robert K.',
    service: 'Hot Stone Therapy',
    rating: 5,
  },
  {
    quote:
      "I've been coming here for my prenatal massages and couldn't be happier. Emily is amazing and makes me feel so comfortable.",
    clientName: 'Amanda L.',
    service: 'Prenatal Massage',
    rating: 5,
  },
  {
    quote:
      'Finally found my go-to massage place! The sports massage has helped my recovery tremendously.',
    clientName: 'Chris T.',
    service: 'Sports Massage',
    rating: 5,
  },
];

export interface TestimonialsSectionProps {
  /**
   * Additional CSS classes
   */
  className?: string;
}

export const TestimonialsSection: FC<TestimonialsSectionProps> = ({ className }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const scrollAmount = 360; // Approximate card width + gap

    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  return (
    <Section
      spacing="lg"
      className={cn('bg-primary-50/30 dark:bg-slate-800/50', className)}
      aria-labelledby="testimonials-heading"
    >
      {/* Section Header */}
      <div className="mb-12 text-center">
        <h2
          id="testimonials-heading"
          className="mb-4 font-serif text-3xl font-bold text-gray-900 dark:text-slate-100 sm:text-4xl"
        >
          What Our Clients Say
        </h2>
        <p className="mx-auto max-w-2xl text-lg text-gray-600 dark:text-slate-400">
          Hear from our valued clients about their experiences and the difference our treatments
          have made in their lives.
        </p>
      </div>

      {/* Testimonials Container */}
      <div className="relative">
        {/* Navigation Buttons - Hidden on mobile, visible on larger screens */}
        <div className="hidden lg:block">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => scroll('left')}
            className="absolute -left-4 top-1/2 z-10 -translate-y-1/2 rounded-full p-2 shadow-md"
            aria-label="Scroll testimonials left"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => scroll('right')}
            className="absolute -right-4 top-1/2 z-10 -translate-y-1/2 rounded-full p-2 shadow-md"
            aria-label="Scroll testimonials right"
          >
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </Button>
        </div>

        {/* Scrollable Container (mobile) / Grid (desktop) */}
        <div
          ref={scrollContainerRef}
          className={cn(
            // Mobile: horizontal scroll
            'flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory',
            // Hide scrollbar but keep functionality
            'scrollbar-hide [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            // Desktop: grid layout
            'lg:grid lg:grid-cols-2 lg:gap-6 lg:overflow-visible lg:pb-0',
            'xl:grid-cols-4'
          )}
        >
          {mockTestimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              {...testimonial}
              className="snap-center flex-shrink-0 lg:flex-shrink"
            />
          ))}
        </div>

        {/* Mobile scroll indicator dots */}
        <div className="mt-6 flex justify-center gap-2 lg:hidden">
          {mockTestimonials.map((_, index) => (
            <span
              key={index}
              className="h-2 w-2 rounded-full bg-primary-300 dark:bg-primary-600"
              aria-hidden="true"
            />
          ))}
        </div>
      </div>
    </Section>
  );
};

TestimonialsSection.displayName = 'TestimonialsSection';
